import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Columns3, Image as ImageIcon, Monitor, Plus, Smartphone, Trash2, Undo2,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  createMenuCard, createMenuColumn, createMenuEntry, createMenuLink, loadHub, loadMenu, loadProducts, loadTranslationCatalogue,
  setMenuCardOrder, setMenuColumnOrder, setMenuEntryOrder, setMenuLinkOrder, updateMenuCard, updateMenuColumn, updateMenuEntry,
  updateMenuLink,
} from './api';
import {
  LinkField, PhotoField, WordField, readLink,
} from './HubFields';
import { Arrows, Line } from './HubPageEditor';
import { tx } from './i18n';
import MenuLive from './MenuLive';
import {
  Alert, Chooser, Failed, Head, Loading, Pill, XomicsSelect,
} from './parts';
import { Switch } from './ProductOptions';
import {
  ENGLISH, SITE_TEXT, activeLanguages, languageLabel, ownWords, saveTexts, unix,
} from './siteWords';
import SiteLanguage from './SiteLanguage';
import { ForStoreOffice, StoreOfficeLine } from './StoreOffice';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import './productDetail.css';
import './help-centre.css';
import './hub.css';
import './menu.css';

// The store's menu, written here: the names across the top of every page and
// the panel each opens.
//
// XOmics keeps the names of the top row, each with the columns of its panel,
// their links and its picture cards (/Menu); the words are storefront site
// text, written through the translation endpoints one language at a time,
// like the Hub's. A row is added, moved, switched off or removed at once, and
// so is where it leads and what a card shows; words are typed and then saved
// together. The store's own bar is shown above the fields with the panel of
// the name being written open, and shows every change, saved or not.

// A link's small tag is part of its text, as the store's text keeps it.
const TAG = /^([^<]*)<0>([^<]*)<\/0>\s*$/;
const partsOf = (value) => {
  const tagged = TAG.exec(value || '');
  return tagged ? { name: tagged[1], tag: tagged[2] } : { name: value || '', tag: '' };
};
const joined = (name, tag) => (tag.trim() === '' ? name : `${name}<0>${tag.trim()}</0>`);
const bare = (value) => partsOf(value).name;

// The looks of a product that its photos show: each with the first photo of it.
function looksOf(product) {
  const looks = new Map();
  for (const image of product?.images || []) {
    if (!(image.places || []).includes('gallery')) continue;
    for (const shown of image.optionValues || []) {
      if (!looks.has(shown.value)) looks.set(shown.value, image.variants?.thumbnail?.url || image.url);
    }
  }
  return [...looks].map(([value, url]) => ({ value, url }));
}

const NOWHERE = { front: null, pages: [] };
// A part pointed at in the store's bar: its fields have opened after the first, and its mark goes after the second.
const POINT_MS = 80;
const POINTED_MS = 2400;

// The menu is one, shared by the offices; which of its names and parts an
// office's store shows is that office's own, so the page is one office's.
export default function XomicsMenu() {
  return <ForStoreOffice title={tx('ui.menu.title')} className="xo-platform-page xo-help-page xo-hub xo-sitemenu">
    {(store) => <MenuPage store={store} />}
  </ForStoreOffice>;
}

function MenuPage({ store }) {
  const { permissions } = useXomicsAuth();
  const canManage = permissions.includes('CONTENT_MANAGE');
  const [chosen, setChosen] = useState(ENGLISH);
  const [entryId, setEntryId] = useState(null);
  // The list of the top row's names, under the name that is open.
  const [choosing, setChoosing] = useState(false);
  // The store's bar as a computer shows it, or as a phone does.
  const [view, setView] = useState('computer');
  // What was pointed at in the store's bar: whose fields (`at`) and which of them (`piece`).
  const [pointed, setPointed] = useState(null);
  const fields = useRef(null);
  // The name typed at the foot of the list, and the entry just made from it.
  const [newName, setNewName] = useState('');
  const [made, setMade] = useState(null);
  const [open, setOpen] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [undo, setUndo] = useState(null);
  const catalogue = useLiveResource(['translations'], useCallback(() => loadTranslationCatalogue(), []));
  const one = useLiveResource(['translations'], useCallback(() => loadMenu(chosen), [chosen]));
  const sold = useLiveResource(['products'], useCallback(() => loadProducts(), []));
  const hubList = useLiveResource(['translations'], useCallback(() => loadHub(ENGLISH), []));
  const menu = one.data;
  // The fields are in the language their words were read in: another language
  // chosen shows in them once its words have arrived, never half of each.
  const language = menu?.language ?? chosen;
  const languages = useMemo(() => (catalogue.data ? activeLanguages(catalogue.data) : []), [catalogue.data]);
  const siteText = catalogue.data?.contentTypes.find((entry) => entry.code === SITE_TEXT) ?? null;
  const products = useMemo(() => sold.data || [], [sold.data]);
  const hub = hubList.data || NOWHERE;

  useEffect(() => { setDrafts({}); setNotice(null); setUndo(null); }, [language]);
  const entries = useMemo(() => menu?.entries || [], [menu]);
  const entry = entries.find((row) => row.id === entryId) || entries[0] || null;

  const texts = useMemo(() => {
    const byKey = new Map();
    const take = (words) => Object.values(words).forEach((text) => byKey.set(text.key, text));
    for (const row of entries) {
      take(row.words);
      row.columns.forEach((column) => { take(column.words); column.links.forEach((link) => take(link.words)); });
      row.cards.forEach((card) => take(card.words));
    }
    return byKey;
  }, [entries]);
  const stored = useCallback((text) => ownWords(text, language), [language]);
  const valueOf = useCallback((text) => (drafts[text.key] !== undefined ? drafts[text.key] : stored(text)), [drafts, stored]);
  const type = useCallback((text, value) => setDrafts((current) => ({ ...current, [text.key]: value })), []);
  const dirty = useMemo(() => Object.keys(drafts).filter((key) => texts.has(key) && unix(drafts[key]) !== stored(texts.get(key))),
    [drafts, stored, texts]);
  useEffect(() => {
    if (dirty.length === 0) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty.length]);

  const refresh = one.refresh;
  const act = useCallback(async (work, done) => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await work();
      await refresh();
      if (done) done(result);
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  // What the store's frame is handed: the menu a visitor would see with the
  // English as it stands here now, the words by key in the language being
  // written, and the entry whose panel is open.
  const other = language !== ENGLISH;
  const sent = useMemo(() => {
    const english = (text) => (!other && drafts[text.key] !== undefined ? drafts[text.key] : text.value);
    const say = (text) => ({ key: text.key, value: english(text) });
    const pages = new Map([...(hub.front ? [hub.front] : []), ...hub.pages].filter((page) => page.isPublished !== false).map((page) => [page.id, page.path]));
    const leads = (link) => {
      const found = readLink(link);
      if (found.mode === 'none') return { link: null, href: null, productId: null };
      if (found.mode === 'page') return { link, href: pages.has(found.pageId) ? `${pages.get(found.pageId)}${found.part ? `#${found.part}` : ''}` : null, productId: null };
      if (found.mode === 'product') return { link, href: null, productId: found.productId };
      return { link, href: link, productId: null };
    };
    const reachable = (row) => row.href !== null || row.productId !== null;
    const words = {};
    for (const [key, text] of texts) words[key] = drafts[key] !== undefined ? drafts[key] : stored(text) || text.value;
    return {
      words,
      open: entry?.code ?? '',
      menu: {
        entries: entries.filter((row) => row.isActive).map((row) => ({
          id: row.id, code: row.code, look: row.look, pins: row.pins, ...leads(row.link), label: say(row.words.label),
          columns: row.columns.filter((column) => column.isActive).map((column) => ({
            id: column.id, lead: column.isLead, ...leads(column.link), title: say(column.words.title),
            links: column.links.filter((link) => link.isActive).map((link) => ({ id: link.id, small: link.isSmall, ...leads(link.link), label: say(link.words.label) }))
              .filter((link) => link.label.value !== '' && reachable(link)),
          })).filter((column) => column.links.length > 0),
          cards: row.cards.filter((card) => card.isActive).map((card) => ({
            id: card.id, ...leads(card.link), option: card.option, photo: card.photo,
            product: card.productId === null ? null : { id: card.productId, code: products.find((product) => product.id === card.productId)?.code ?? null },
            name: say(card.words.name), tag: say(card.words.tag),
          })).filter((card) => card.name.value !== '' && reachable(card)),
        })).filter((row) => row.label.value !== ''),
      },
    };
  }, [drafts, entries, entry, hub, other, products, stored, texts]);
  const lastSent = useRef(sent);
  lastSent.current = sent;

  // A part pointed at in the store's frame opens its fields here, and the
  // field it is written in is the one that is shown.
  const onPick = useCallback((picked) => {
    const shown = lastSent.current.menu.entries.find((row) => row.code === picked.entry);
    if (!shown) return;
    const card = Number.isInteger(picked.card) ? shown.cards[picked.card] ?? null : null;
    const column = Number.isInteger(picked.column) ? shown.columns[picked.column] ?? null : null;
    const link = column && Number.isInteger(picked.link) ? column.links[picked.link] ?? null : null;
    setEntryId(shown.id);
    if (card) setOpen({ card: card.id });
    else if (column) setOpen({ column: column.id, link: link ? link.id : null });
    else setOpen(null);
    setPointed({
      at: card ? `card-${card.id}` : link ? `link-${link.id}` : column ? `column-${column.id}` : 'entry',
      piece: typeof picked.piece === 'string' ? picked.piece : 'name',
      time: Date.now(),
    });
  }, []);

  // A name just made is open with the cursor in its name. Where the server made
  // it without the name that was typed, the name stands in its field to be saved.
  useEffect(() => {
    if (!made) return;
    const row = entries.find((one) => one.id === made.id);
    if (!row) return;
    if (row.words.label.value !== made.name) type(row.words.label, made.name);
    setMade(null);
    setPointed({ at: 'entry', piece: 'name', time: Date.now() });
  }, [entries, made, type]);

  // What was pointed at is brought into view with the cursor in it, and marked for a moment.
  useEffect(() => {
    if (!pointed) return undefined;
    let marked = null;
    const show = window.setTimeout(() => {
      const owner = fields.current?.querySelector(`[data-pick="${pointed.at}"]`);
      if (!owner) return;
      marked = owner.querySelector(`[data-piece="${pointed.piece}"]`) || owner;
      marked.scrollIntoView({ block: 'center', behavior: 'smooth' });
      marked.querySelector('.xo-field, .xo-select-trigger')?.focus({ preventScroll: true });
      marked.classList.add('is-pointed');
    }, POINT_MS);
    const clear = window.setTimeout(() => marked?.classList.remove('is-pointed'), POINTED_MS);
    return () => { window.clearTimeout(show); window.clearTimeout(clear); marked?.classList.remove('is-pointed'); };
  }, [pointed]);

  if ((one.error && !menu) || (catalogue.error && !catalogue.data)) {
    return <Failed message={one.error || catalogue.error} onRetry={() => { one.refresh(); catalogue.refresh(); }} />;
  }
  if (!menu || !catalogue.data || !siteText) return <Loading label={tx('ui.menu.loading')} />;

  const write = canManage && !busy;
  async function save() {
    setBusy(true);
    setNotice(null);
    try {
      await saveTexts(dirty.map((key) => [texts.get(key), unix(drafts[key])]), { language, contentTypeId: siteText.id, source: 'menu' });
      await one.refresh();
      setDrafts({});
      setNotice({ tone: 'ok', text: tx('ui.hub.save.saved') });
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }
  const leave = (go) => { if (dirty.length === 0 || window.confirm(tx('ui.hub.save.leave'))) { setDrafts({}); go(); } };
  // The name typed under the list becomes a new name of the top row, opened at once.
  const addEntry = () => {
    const name = newName.trim();
    if (name === '') return;
    leave(() => act(() => createMenuEntry(name), (created) => {
      setChoosing(false);
      setNewName('');
      setEntryId(created.id);
      setOpen(null);
      setMade({ id: created.id, name });
    }));
  };
  const moved = (list, index, by) => {
    const ids = list.map((row) => row.id);
    [ids[index], ids[index + by]] = [ids[index + by], ids[index]];
    return ids;
  };
  const putBack = () => act(() => {
    if (undo.link) return updateMenuLink(undo.link, { isRemoved: false });
    if (undo.column) return updateMenuColumn(undo.column, { isRemoved: false });
    if (undo.card) return updateMenuCard(undo.card, { isRemoved: false });
    return updateMenuEntry(undo.entry, { isRemoved: false });
  }, () => setUndo(null));
  const editor = {
    write, act, language, valueOf, type, hub, products, onRemoved: setUndo,
  };
  const nameOf = (text) => bare(valueOf(text) || text.value) || tx('ui.hub.section.empty');

  return <div className="xo-platform-page xo-help-page xo-hub xo-sitemenu">
    <Head title={tx('ui.menu.title')} under={<StoreOfficeLine store={store} />} />
    {!canManage && <Alert tone="info">{tx('ui.menu.read_only')}</Alert>}
    {/* Which name of the top row is open is chosen from a list under it, so its panel has the whole
        width; a new name is typed at the foot of that list. */}
    <div className="xo-product-navigation">
    <div className="xo-product-section-row">
      <Chooser label={tx('ui.menu.side.top')} name={entry ? nameOf(entry.words.label) : tx('ui.menu.side.top')}
        open={choosing} onOpen={setChoosing}
        footer={canManage && <form className="xo-chooser-add" onSubmit={(event) => { event.preventDefault(); addEntry(); }}>
          <input className="xo-field" type="text" value={newName} maxLength={60} disabled={!write}
            aria-label={tx('ui.menu.entry.add')} placeholder={tx('ui.menu.entry.add')}
            onChange={(event) => setNewName(event.target.value)} />
          <button type="submit" className="xo-btn" disabled={!write || newName.trim() === ''}>
            <Plus aria-hidden="true" />{tx('ui.products.options.add')}</button>
        </form>}>
      <nav className="xo-help-side xo-hub-side" aria-label={tx('ui.menu.title')}>
        <ul>
          {entries.map((row, index) => <li key={row.id}>
            <div className="xo-hub-side-category">
              <button type="button" className={`${entry && entry.id === row.id ? 'is-on' : ''}${row.isActive ? '' : ' is-draft'}`}
                onClick={() => { setChoosing(false); leave(() => { setEntryId(row.id); setOpen(null); }); }}>
                <span dir="auto">{nameOf(row.words.label)}</span>
                {!row.isActive && <small>{tx('ui.menu.hidden')}</small>}
              </button>
              {canManage && <Arrows canUp={index > 0} canDown={index < entries.length - 1} disabled={!write}
                onUp={() => act(() => setMenuEntryOrder(moved(entries, index, -1)))}
                onDown={() => act(() => setMenuEntryOrder(moved(entries, index, 1)))} />}
            </div>
          </li>)}
        </ul>
      </nav>
      </Chooser>
      <div className="xo-product-toolbar-actions">
        <div className="xo-pm-live-views" role="radiogroup" aria-label={tx('ui.products.page.live.view')}>
          <button type="button" role="radio" aria-checked={view === 'computer'} className={view === 'computer' ? 'is-on' : ''}
            aria-label={tx('ui.products.page.live.computer')} title={tx('ui.products.page.live.computer')}
            onClick={() => setView('computer')}><Monitor aria-hidden="true" /></button>
          <button type="button" role="radio" aria-checked={view === 'phone'} className={view === 'phone' ? 'is-on' : ''}
            aria-label={tx('ui.products.page.live.phone')} title={tx('ui.products.page.live.phone')}
            onClick={() => setView('phone')}><Smartphone aria-hidden="true" /></button>
        </div>
        <SiteLanguage label={tx('ui.hub.page.language')} value={chosen} languages={languages}
          onChange={(code) => { if (dirty.length === 0 || window.confirm(tx('ui.hub.save.leave'))) setChosen(code); }} />
      </div>
    </div>
    </div>

      <div className="xo-help-main">
        {entry && <>
          {other && <Alert tone="info">{tx('ui.hub.page.language_note', { language: languageLabel(languages.find((row) => row.code === language) || { name: language }) })}</Alert>}
          {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
          {undo && <Alert tone="info">{tx('ui.hub.section.removed')}{' '}
            <button type="button" className="xo-link" disabled={!write} onClick={putBack}><Undo2 aria-hidden="true" />{tx('ui.hub.section.undo')}</button></Alert>}

          <MenuLive sent={sent} onPick={onPick} view={view} />

          <div className="xo-hub-cards" ref={fields}>
            <section className="xo-hub-settings" data-pick="entry">
              <div className="xo-sitemenu-pair">
                <div data-piece="name">
                  <WordField kind="line" label={tx('ui.menu.entry.name')} text={entry.words.label} language={language}
                    value={valueOf(entry.words.label)} disabled={!write} hub={NOWHERE} products={[]}
                    onChange={(value) => type(entry.words.label, value)} />
                </div>
                <label className="xo-hub-field">
                  <span className="xo-hub-label">{tx('ui.menu.entry.look')}</span>
                  <XomicsSelect value={entry.look} disabled={!write} aria-label={tx('ui.menu.entry.look')}
                    onChange={(event) => act(() => updateMenuEntry(entry.id, { look: event.target.value }))}>
                    {menu.looks.map((look) => <option key={look} value={look}>{tx(`ui.menu.look.${look}`)}</option>)}
                  </XomicsSelect>
                </label>
              </div>
              <LinkField key={`entry-${entry.id}`} label={tx('ui.hub.link.title')} value={entry.link} hub={hub} products={products} disabled={!write}
                onChange={(link) => act(() => updateMenuEntry(entry.id, { link }))} />
              <div className="xo-sitemenu-flag">
                <span>{tx('ui.menu.entry.pins')}</span>
                <Switch on={entry.pins} disabled={!write} label={tx('ui.menu.entry.pins')}
                  onChange={(pins) => act(() => updateMenuEntry(entry.id, { pins }))} />
              </div>
              {/* Off, the name is kept here and left out of the store's bar. */}
              <div className="xo-sitemenu-flag">
                <span>{tx('ui.menu.entry.shown')}</span>
                <Switch on={entry.isActive} disabled={!write} label={tx('ui.menu.entry.shown')}
                  onChange={(isActive) => act(() => updateMenuEntry(entry.id, { isActive }))} />
              </div>
              {/* Taking the name away altogether is the one red line at the foot. */}
              <div className="xo-hub-card-foot">
                <button type="button" className="xo-link is-danger" disabled={!write}
                  onClick={() => act(() => updateMenuEntry(entry.id, { isRemoved: true }), () => { setUndo({ entry: entry.id }); setEntryId(null); })}>
                  <Trash2 aria-hidden="true" />{tx('ui.staff.delete_f6fdbe48')}</button>
              </div>
            </section>

            <p className="xo-hub-side-label">{tx('ui.menu.columns')}</p>
            {entry.columns.map((column, index) => <ColumnCard key={column.id} column={column} editor={editor}
              open={open?.column === column.id} openLink={open?.column === column.id ? open.link : null}
              onToggle={() => setOpen(open?.column === column.id ? null : { column: column.id, link: null })}
              onLink={(link) => setOpen({ column: column.id, link })}
              canUp={index > 0} canDown={index < entry.columns.length - 1}
              onUp={() => act(() => setMenuColumnOrder(entry.id, moved(entry.columns, index, -1)))}
              onDown={() => act(() => setMenuColumnOrder(entry.id, moved(entry.columns, index, 1)))} />)}
            <button type="button" className="xo-btn-2 xo-hub-add" disabled={!write}
              onClick={() => act(() => createMenuColumn(entry.id), (created) => setOpen({ column: created.id, link: null }))}>
              <Plus aria-hidden="true" />{tx('ui.menu.column.add')}</button>

            <p className="xo-hub-side-label">{tx('ui.menu.cards')}</p>
            {entry.cards.map((card, index) => <PictureCard key={card.id} card={card} editor={editor}
              open={open?.card === card.id} onToggle={() => setOpen(open?.card === card.id ? null : { card: card.id })}
              canUp={index > 0} canDown={index < entry.cards.length - 1}
              onUp={() => act(() => setMenuCardOrder(entry.id, moved(entry.cards, index, -1)))}
              onDown={() => act(() => setMenuCardOrder(entry.id, moved(entry.cards, index, 1)))} />)}
            <button type="button" className="xo-btn-2 xo-hub-add" disabled={!write}
              onClick={() => act(() => createMenuCard(entry.id), (created) => setOpen({ card: created.id }))}>
              <Plus aria-hidden="true" />{tx('ui.menu.card.add')}</button>

            {dirty.length > 0 && <div className="xo-hub-save" role="status">
              <span>{tx('ui.hub.save.dirty', { count: dirty.length })}</span>
              <button type="button" className="xo-btn-2" disabled={busy} onClick={() => setDrafts({})}>{tx('ui.hub.save.discard')}</button>
              <button type="button" className="xo-btn" disabled={!write} onClick={save}>{busy ? tx('ui.hub.save.saving') : tx('ui.hub.save.save')}</button>
            </div>}
          </div>
        </>}
        {!entry && notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        {!entry && undo && <Alert tone="info">{tx('ui.hub.section.removed')}{' '}
          <button type="button" className="xo-link" disabled={!write} onClick={putBack}><Undo2 aria-hidden="true" />{tx('ui.hub.section.undo')}</button></Alert>}
      </div>
  </div>;
}

// A column of a panel: its title, which may be a link itself, and its links.
function ColumnCard({
  column, editor, open, openLink, onToggle, onLink, canUp, canDown, onUp, onDown,
}) {
  const {
    write, act, language, valueOf, type, hub, products, onRemoved,
  } = editor;
  const title = bare(valueOf(column.words.title) || column.words.title.value);
  const names = column.links.map((link) => bare(valueOf(link.words.label) || link.words.label.value)).filter(Boolean);
  const order = (index, by) => {
    const ids = column.links.map((link) => link.id);
    [ids[index], ids[index + by]] = [ids[index + by], ids[index]];
    act(() => setMenuLinkOrder(column.id, ids));
  };
  return <section className={`xo-hub-card${open ? ' is-open' : ''}${column.isActive ? '' : ' is-off'}`} data-pick={`column-${column.id}`}
    aria-label={`${tx('ui.menu.column.name')}${title ? `: ${title}` : ''}`}>
    <Line className="xo-hub-card-head" open={open} onToggle={onToggle}
      arrows={<Arrows canUp={canUp} canDown={canDown} disabled={!write} onUp={onUp} onDown={onDown} />}
      picture={<span className="xo-hub-card-icon"><Columns3 aria-hidden="true" /></span>}
      name={title || tx('ui.menu.column.name')} words={names.join(', ')}
      pills={!column.isActive && <Pill tone="flat">{tx('ui.hub.section.off')}</Pill>}
      shown={<Switch on={column.isActive} disabled={!write} label={tx('ui.menu.entry.shown')}
        onChange={(isActive) => act(() => updateMenuColumn(column.id, { isActive }))} />} />
    {open && <div className="xo-hub-card-body">
      <div data-piece="title">
        <WordField kind="line" label={tx('ui.menu.column.title')} text={column.words.title} language={language}
          value={valueOf(column.words.title)} disabled={!write} hub={NOWHERE} products={[]}
          onChange={(value) => type(column.words.title, value)} />
      </div>
      <LinkField label={tx('ui.menu.column.leads')} value={column.link} hub={hub} products={products} disabled={!write}
        onChange={(link) => act(() => updateMenuColumn(column.id, { link }))} />
      <div className="xo-sitemenu-flag">
        <span>{tx('ui.menu.column.lead')}</span>
        <Switch on={column.isLead} disabled={!write} label={tx('ui.menu.column.lead')}
          onChange={(isLead) => act(() => updateMenuColumn(column.id, { isLead }))} />
      </div>

      <div className="xo-hub-items">
        {column.links.map((link, index) => <LinkRow key={link.id} link={link} editor={editor}
          open={openLink === link.id} onToggle={() => onLink(openLink === link.id ? null : link.id)}
          canUp={index > 0} canDown={index < column.links.length - 1} onUp={() => order(index, -1)} onDown={() => order(index, 1)} />)}
        <div className="xo-hub-items-add">
          <button type="button" className="xo-btn-2" disabled={!write}
            onClick={() => act(() => createMenuLink(column.id), (created) => onLink(created.id))}>
            <Plus aria-hidden="true" />{tx('ui.menu.link.add')}</button>
        </div>
      </div>

      <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateMenuColumn(column.id, { isRemoved: true }), () => onRemoved({ column: column.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.menu.column.remove')}</button>
      </div>
    </div>}
  </section>;
}

// One link of a column: its name, the small tag beside it, and where it leads.
function LinkRow({
  link, editor, open, onToggle, canUp, canDown, onUp, onDown,
}) {
  const {
    write, act, language, valueOf, type, hub, products, onRemoved,
  } = editor;
  const text = link.words.label;
  const now = partsOf(valueOf(text));
  const english = partsOf(text.value);
  const leads = readLink(link.link);
  const where = leads.mode === 'page' ? [hub.front, ...hub.pages].find((page) => page && page.id === leads.pageId)?.path
    : leads.mode === 'product' ? products.find((product) => product.id === leads.productId)?.name : link.link;
  return <div className={`xo-hub-item${open ? ' is-open' : ''}${link.isActive ? '' : ' is-off'}`} data-pick={`link-${link.id}`}>
    <Line className="xo-hub-item-head" open={open} onToggle={onToggle}
      arrows={<Arrows canUp={canUp} canDown={canDown} disabled={!write} onUp={onUp} onDown={onDown} />}
      name={now.name || english.name || tx('ui.hub.section.empty')} words={where || tx('ui.hub.link.none')}
      shown={<Switch on={link.isActive} disabled={!write} label={tx('ui.menu.entry.shown')}
        onChange={(isActive) => act(() => updateMenuLink(link.id, { isActive }))} />} />
    {open && <div className="xo-hub-item-body">
      <div className="xo-sitemenu-pair">
        <div data-piece="name">
          <WordField kind="line" label={tx('ui.menu.link.label')} text={{ value: english.name }} language={language}
            value={now.name} disabled={!write} hub={NOWHERE} products={[]} onChange={(name) => type(text, joined(name, now.tag))} />
        </div>
        <div data-piece="tag">
          <WordField kind="line" label={tx('ui.menu.link.tag')} text={{ value: english.tag }} language={language}
            value={now.tag} disabled={!write} hub={NOWHERE} products={[]} onChange={(tag) => type(text, joined(now.name, tag))} />
        </div>
      </div>
      <LinkField label={tx('ui.hub.link.title')} value={link.link} hub={hub} products={products} disabled={!write}
        onChange={(next) => act(() => updateMenuLink(link.id, { link: next }))} />
      <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateMenuLink(link.id, { isRemoved: true }), () => onRemoved({ link: link.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.menu.link.remove')}</button>
      </div>
    </div>}
  </div>;
}

// A picture card of a panel: its name, the tag on its picture, where it leads,
// and what it shows: one of our products (and which of its looks), or a photo
// of its own.
function PictureCard({
  card, editor, open, onToggle, canUp, canDown, onUp, onDown,
}) {
  const {
    write, act, language, valueOf, type, hub, products, onRemoved,
  } = editor;
  const [own, setOwn] = useState(card.productId === null && card.photo !== null);
  useEffect(() => { setOwn(card.productId === null && card.photo !== null); }, [card.productId, card.photo]);
  const product = products.find((row) => row.id === card.productId) || null;
  const looks = looksOf(product);
  const set = (changes) => act(() => updateMenuCard(card.id, changes));
  const name = bare(valueOf(card.words.name) || card.words.name.value);
  const thumb = card.photo?.thumbnailUrl || card.photo?.url
    || (looks.find((look) => look.value === card.option)?.url ?? product?.mainImageThumbnailUrl ?? product?.imageUrl ?? null);
  return <section className={`xo-hub-card${open ? ' is-open' : ''}${card.isActive ? '' : ' is-off'}`} data-pick={`card-${card.id}`}
    aria-label={`${tx('ui.menu.card.name')}${name ? `: ${name}` : ''}`}>
    <Line className="xo-hub-card-head" open={open} onToggle={onToggle}
      arrows={<Arrows canUp={canUp} canDown={canDown} disabled={!write} onUp={onUp} onDown={onDown} />}
      picture={thumb ? <img className="xo-hub-item-thumb" src={thumb} alt="" />
        : <span className="xo-hub-card-icon"><ImageIcon aria-hidden="true" /></span>}
      name={name || tx('ui.menu.card.name')} words={product ? product.name : ''}
      pills={!card.isActive && <Pill tone="flat">{tx('ui.hub.section.off')}</Pill>}
      shown={<Switch on={card.isActive} disabled={!write} label={tx('ui.menu.entry.shown')} onChange={(isActive) => set({ isActive })} />} />
    {open && <div className="xo-hub-card-body">
      <div className="xo-sitemenu-pair">
        <div data-piece="name">
          <WordField kind="line" label={tx('ui.menu.card.title')} text={card.words.name} language={language}
            value={valueOf(card.words.name)} disabled={!write} hub={NOWHERE} products={[]} onChange={(value) => type(card.words.name, value)} />
        </div>
        <div data-piece="tag">
          <WordField kind="line" label={tx('ui.menu.card.tag')} text={card.words.tag} language={language}
            value={valueOf(card.words.tag)} disabled={!write} hub={NOWHERE} products={[]} onChange={(value) => type(card.words.tag, value)} />
        </div>
      </div>
      <LinkField label={tx('ui.hub.link.title')} value={card.link} hub={hub} products={products} disabled={!write}
        onChange={(link) => set({ link })} />

      <div className="xo-sitemenu-pair" data-piece="picture">
        <label className="xo-hub-field">
          <span className="xo-hub-label">{tx('ui.menu.card.picture')}</span>
          <XomicsSelect value={own ? 'own' : 'product'} disabled={!write} aria-label={tx('ui.menu.card.picture')}
            onChange={(event) => setOwn(event.target.value === 'own')}>
            <option value="product">{tx('ui.menu.card.from_product')}</option>
            <option value="own">{tx('ui.menu.card.own')}</option>
          </XomicsSelect>
        </label>
        {!own && <label className="xo-hub-field">
          <span className="xo-hub-label">{tx('ui.menu.card.product')}</span>
          <XomicsSelect value={card.productId ?? ''} disabled={!write} aria-label={tx('ui.menu.card.product')}
            onChange={(event) => { if (event.target.value !== '') set({ productId: Number(event.target.value) }); }}>
            <option value="">{tx('ui.hub.link.choose_product')}</option>
            {products.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </XomicsSelect>
        </label>}
      </div>
      {!own && looks.length > 1 && <div className="xo-hub-field">
        <span className="xo-hub-label">{tx('ui.menu.card.look')}</span>
        <div className="xo-sitemenu-looks" role="radiogroup" aria-label={tx('ui.menu.card.look')}>
          {looks.map((look) => {
            const on = card.option === look.value;
            return <button key={look.value} type="button" role="radio" aria-checked={on} className={on ? 'is-on' : ''} disabled={!write}
              aria-label={look.value} title={look.value} onClick={() => set({ option: look.value })}>
              <img src={look.url} alt="" /></button>;
          })}
        </div>
      </div>}
      {own && <PhotoField photo={card.photo} canFocus disabled={!write} onSet={set} />}

      <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateMenuCard(card.id, { isRemoved: true }), () => onRemoved({ card: card.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.menu.card.remove')}</button>
      </div>
    </div>}
  </section>;
}
